import { supabase } from '@/lib/supabase';
import { porIdiomaDelPais, customerDocOf } from '@/lib/country';

/**
 * Textos del correo de financiamiento. Lo recibe la AUTOMOTORA: va en el
 * idioma de su país. `ES` son los literales de siempre, carácter por carácter
 * (un tenant chileno o sin país recibe exactamente el mismo HTML que antes).
 */
const TXT_FINANCIAMIENTO_ES = {
  lang: 'es',
  title: 'Nuevo Lead de Financiamiento',
  h1: 'Nuevo Lead: Financiamiento',
  clientInfo: 'Información del Cliente',
  name: 'Nombre:',
  email: 'Email:',
  phone: 'Teléfono:',
  rut: 'Rut:',
  birthDate: 'Fecha de nacimiento:',
  employment: 'Actividad laboral:',
  income: 'Renta líquida mensual:',
  downPayment: 'Pie:',
  vehicleDetails: 'Detalles del Vehículo',
  brand: 'Marca:',
  model: 'Modelo:',
  year: 'Año:',
  plate: 'Placa:',
  price: 'Precio:',
  customerMessage: 'Mensaje del cliente',
  cta: 'Ver detalles en el Portal',
  footer1: 'Este es un email automático generado por',
  footer2: 'Por favor no responda a este mensaje.',
  notSpecified: 'No especificado',
  notSpecifiedF: 'No especificada',
};
const TXT_FINANCIAMIENTO_PT: typeof TXT_FINANCIAMIENTO_ES = {
  lang: 'pt-BR',
  title: 'Novo lead de financiamento',
  h1: 'Novo lead: Financiamento',
  clientInfo: 'Informações do cliente',
  name: 'Nome:',
  email: 'E-mail:',
  phone: 'Telefone:',
  rut: 'CPF:',
  birthDate: 'Data de nascimento:',
  employment: 'Situação profissional:',
  income: 'Renda líquida mensal:',
  downPayment: 'Entrada:',
  vehicleDetails: 'Detalhes do veículo',
  brand: 'Marca:',
  model: 'Modelo:',
  year: 'Ano:',
  plate: 'Placa:',
  price: 'Preço:',
  customerMessage: 'Mensagem do cliente',
  cta: 'Ver detalhes no Portal',
  footer1: 'Este é um e-mail automático gerado pela',
  footer2: 'Por favor, não responda a esta mensagem.',
  notSpecified: 'Não informado',
  notSpecifiedF: 'Não informada',
};
/** EE.UU. (por país del TENANT, nunca por el idioma del visitante). */
const TXT_FINANCIAMIENTO_EN: typeof TXT_FINANCIAMIENTO_ES = {
  lang: 'en-US',
  title: 'New financing lead',
  h1: 'New lead: Financing',
  clientInfo: 'Customer information',
  name: 'Name:',
  email: 'Email:',
  phone: 'Phone:',
  // Sin uso en EE.UU.: el rótulo del documento sale de `customerDocOf` ("ID/EIN:").
  rut: 'ID:',
  birthDate: 'Date of birth:',
  employment: 'Employment:',
  income: 'Monthly net income:',
  downPayment: 'Down payment:',
  vehicleDetails: 'Vehicle details',
  brand: 'Make:',
  model: 'Model:',
  year: 'Year:',
  plate: 'License plate:',
  price: 'Price:',
  customerMessage: 'Customer message',
  cta: 'View details in the Portal',
  footer1: 'This is an automated email sent by',
  footer2: 'Please do not reply to this message.',
  notSpecified: 'Not provided',
  notSpecifiedF: 'Not provided',
};

export function createFinancingLeadEmailTemplate({
  customerName,
  customerEmail,
  customerPhone,
  customerRut,
  vehicleDetails,
  additionalMessage,
  currencySymbol = '$',
  country,
}: {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerRut: string;
  vehicleDetails: {
    brand: string;
    model: string;
    year: string;
    plate?: string;
    price?: string;
  };
  additionalMessage?: string;
  /** Símbolo de la moneda del tenant ('$' CLP/USD, 'R$' BRL). */
  currencySymbol?: string;
  /** País del tenant (clients.country). 'BR' → correo en portugués; el resto, el de siempre. */
  country?: string | null;
}): string {
  const L = porIdiomaDelPais(country, {
    es: TXT_FINANCIAMIENTO_ES,
    pt: TXT_FINANCIAMIENTO_PT,
    en: TXT_FINANCIAMIENTO_EN,
  });
  // Rótulo del documento por PAÍS: CO/AR nombran el suyo ("NIT/Cédula:",
  // "CUIT/DNI:"); CL ("Rut:") y BR ("CPF:") quedan los de siempre.
  const docPais = customerDocOf(country);
  const rotuloDoc = docPais ? `${docPais.label}:` : L.rut;
  // Formatear los valores del vehículo para mejor visualización
  const formattedVehicleDetails = {
    brand: vehicleDetails.brand || L.notSpecified,
    model: vehicleDetails.model || L.notSpecified,
    year: vehicleDetails.year || L.notSpecified,
    plate: vehicleDetails.plate || L.notSpecified,
    price: vehicleDetails.price
      ? `${currencySymbol}${vehicleDetails.price}`
      : L.notSpecified,
  };

  // Extraer información financiera del mensaje adicional.
  // Los rótulos que se buscan ("- Actividad Laboral:", "Mensaje del cliente:"…)
  // son un formato INTERNO que arma FinancingForm siempre en español, también
  // en Brasil: no se muestran, sólo se usan para separar los datos.
  let actividadLaboral = L.notSpecified;
  let rentaMensual = L.notSpecified;
  let pie = L.notSpecified;
  let fechaNacimiento = L.notSpecifiedF;
  let mensajeCliente = '';
  if (additionalMessage) {
    const matchActividad = additionalMessage.match(/- Actividad Laboral: (.*)/);
    // El monto llega CON el símbolo del tenant desde FinancingForm ('$' o 'R$'),
    // así que se captura completo en vez de asumir un '$' literal.
    const matchRenta = additionalMessage.match(
      /- Renta Líquida Mensual: (.*)/
    );
    const matchPie = additionalMessage.match(/- Monto Pie: (.*)/);
    const matchFecha = additionalMessage.match(/- Fecha de Nacimiento: (.*)/);
    const matchMensaje = additionalMessage.match(
      /Mensaje del cliente:\n([\s\S]*)/
    );
    if (matchActividad) actividadLaboral = matchActividad[1].trim();
    if (matchRenta) rentaMensual = matchRenta[1].trim();
    if (matchPie) pie = matchPie[1].trim();
    if (matchFecha) fechaNacimiento = matchFecha[1].trim();
    if (matchMensaje) mensajeCliente = matchMensaje[1].trim();
  }

  return `
    <!DOCTYPE html>
    <html lang="${L.lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${L.title}</title>
    </head>
    <body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 650px; margin: 0 auto; padding: 20px; color: #333; background: #f8f9fa;">
      <div style="background: #f1f8fe; border-left: 6px solid #51bde5; border-radius: 10px; padding: 0 0 24px 0;">
        <!-- Header -->
        <div style="background-color: #51bde5; padding: 24px 24px 20px 24px; text-align: center; border-radius: 10px 10px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 26px;">${L.h1}</h1>
        </div>
        <!-- Bloque Cliente -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.clientInfo}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.name}</strong></td><td style="padding: 7px 0;">${customerName}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.email}</strong></td><td style="padding: 7px 0;"><a href="mailto:${customerEmail}" style="color: #51bde5; text-decoration: none;">${customerEmail}</a></td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.phone}</strong></td><td style="padding: 7px 0;"><a href="tel:${customerPhone}" style="color: #51bde5; text-decoration: none;">${customerPhone}</a></td></tr>
            <tr><td style="padding: 7px 0;"><strong>${rotuloDoc}</strong></td><td style="padding: 7px 0;">${
              customerRut || L.notSpecified
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.birthDate}</strong></td><td style="padding: 7px 0;">${fechaNacimiento}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.employment}</strong></td><td style="padding: 7px 0;">${actividadLaboral}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.income}</strong></td><td style="padding: 7px 0;">${rentaMensual}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.downPayment}</strong></td><td style="padding: 7px 0;">${pie}</td></tr>
          </table>
        </div>
        <!-- Bloque Vehículo -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.vehicleDetails}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.brand}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.brand
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.model}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.model
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.year}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.year
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.plate}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.plate
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.price}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.price
            }</td></tr>
          </table>
        </div>
        <!-- Mensaje del cliente -->
        ${
          mensajeCliente
            ? `<div style=\"padding: 24px 24px 0 24px;\"><h2 style=\"color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;\">${L.customerMessage}</h2><div style=\"padding: 10px 0; white-space: pre-wrap; font-size: 15px;\">${mensajeCliente}</div></div>`
            : ''
        }
        <!-- CTA Button -->
        <div style="margin: 30px 0 0 0; text-align: center;">
          <a href="https://portal.goauto.cl/leads" 
             style="background-color: #51bde5; color: white; padding: 12px 25px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block;">
            ${L.cta}
          </a>
        </div>
        <!-- Footer -->
        <div style="background-color: #f1f1f1; padding: 15px; text-align: center; font-size: 13px; color: #666; border-top: 1px solid #ddd; margin-top: 32px; border-radius: 0 0 10px 10px;">
          <p style="margin: 0;">
            ${L.footer1} <a href="https://goauto.cl" style="color: #51bde5; text-decoration: none;">GoAuto</a>. 
            <br>${L.footer2}
          </p>
        </div>
      </div>
    </body>
    </html>
  `;
}

export async function sendEmail(options: {
  to: string[];
  subject: string;
  content: string;
  from?: string;
  template_id?: string;
  template_data?: Record<string, any>;
  // Id de vehículo para que la edge function resuelva la patente y reemplace el
  // placeholder {{VEHICLE_PLATE}} del contenido. El sitio no puede leer
  // license_plate (revocada a anon), así que el dato se resuelve server-side.
  resolve_vehicle_plate?: number;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.functions.invoke('send-email', {
      body: {
        to: options.to,
        subject: options.subject,
        content: options.content,
        from: options.from,
        template_id: options.template_id,
        template_data: options.template_data,
        resolve_vehicle_plate: options.resolve_vehicle_plate,
      },
    });

    if (error) {
      console.warn('Error using Supabase SDK, trying fallback method:', error);
      // Si falla el SDK, intentar con el método de fetch directo
      await sendEmailFallback(options);
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error sending email:', error);
    return {
      success: false,
      error: error.message || 'Error desconocido al enviar el email',
    };
  }
}

async function sendEmailFallback(options: {
  to: string[];
  subject: string;
  content: string;
  from?: string;
  template_id?: string;
  template_data?: Record<string, any>;
  resolve_vehicle_plate?: number;
}): Promise<void> {
  const response = await fetch(
    'https://miuiujntdjrjhhcysiba.supabase.co/functions/v1/send-email',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        to: options.to,
        subject: options.subject,
        content: options.content,
        from: options.from,
        template_id: options.template_id,
        template_data: options.template_data,
        resolve_vehicle_plate: options.resolve_vehicle_plate,
      }),
    }
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Error al enviar el email');
  }
}
